if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // The stock add, exactly as Dawn ships it. A product with no gift runs this and nothing
        // else. Gift with purchase (docs/features/gift-with-purchase.md) wraps it, never edits it.
        const stockAdd = () => fetch(`${routes.cart_add_url}`, config).then((response) => response.json());

        (this.dataset.gwpVariantId ? this.addWithGift(formData, stockAdd) : stockAdd())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Gift with purchase: product and gift in ONE request, so a gift-bearing add costs about
      // the same as an ordinary one. Measured on this store (2026-09-18): ordinary add ~510ms,
      // product-then-gift as two requests ~910ms - and far worse on a theme preview or a
      // mobile connection, where it read as "a good few seconds". Only ~155ms of a request is
      // drawer rendering; the rest is per-request overhead, so halving the requests is the fix.
      //
      // The gift must still never block or alter the purchase, so this path is taken only when
      // it is provably equivalent to the stock add, and otherwise steps aside:
      // - a form field we do not understand -> the stock add, then addGift() as a second
      //   request. Slower, but it cannot drop an app's input on the floor.
      // - a subscription, a gift already in the cart, no cart UI -> the stock add alone.
      // - Shopify rejects the combined add (422: sold out, quantity rule...) -> recoverAdd().
      // - the network fails -> rethrown, exactly as the stock add behaves. Never retried: the
      //   server may have processed the first attempt.
      async addWithGift(formData, stockAdd) {
        const item = this.giftAddItem(formData);
        // The drawer on the page can be stale (Back, another tab). A duplicate that slips
        // through is removed by snippets/gwp-line-target.liquid + assets/gwp-guard.js.
        const giftInCart = document.querySelector('cart-drawer [data-gwp-line], cart-items [data-gwp-line]');
        if (!this.cart || giftInCart || item === 'subscription') return stockAdd();
        if (!item) return stockAdd().then((response) => this.addGift(response));

        // How many of this product the cart held as the drawer last rendered it. Only
        // recoverAdd() needs it, but it has to be read before the request changes anything.
        const isProduct = (line) => line.variant_id === item.id && !(line.properties && line.properties._gwp);
        const before = Array.from(
          document.querySelectorAll(`cart-drawer input[data-quantity-variant-id="${item.id}"]`)
        ).reduce((total, input) => total + (Number(input.value) || 0), 0);

        // parent_id nests the gift under the product added in this same request, so Shopify
        // itself removes it when the product goes - including at checkout. Content-Type is set
        // explicitly: the stock config deletes it to let the browser write a multipart boundary.
        const result = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [
              item,
              {
                id: Number(this.dataset.gwpVariantId),
                quantity: 1,
                parent_id: item.id,
                properties: { _gwp: String(item.id) },
              },
            ],
            sections: this.cart.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const data = await result.json();
        if (data.status || !Array.isArray(data.items)) return this.recoverAdd(item, before, isProduct, stockAdd);

        // Hand back the single-add shape every cartUpdate subscriber expects: the product's own
        // line, with the sections that already show both lines.
        return { ...(data.items.find(isProduct) || data.items[0]), sections: data.sections };
      }

      // The combined add was rejected, most likely because the gift sold out after the page was
      // rendered. The purchase must still go through - but Shopify's docs do not say whether a
      // rejected multi-item add is all-or-nothing, and it could not be tested (2026-09-18, the
      // storefront throttled the probe). So this does not assume it: it reads the cart, and
      // re-adds the product only if it is not already there. Getting this wrong the other way
      // would double the quantity on a customer who asked for one. Runs on the failure path
      // only, so its extra request costs an ordinary add nothing.
      async recoverAdd(item, before, isProduct, stockAdd) {
        const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
        const lines = Array.isArray(cart.items) ? cart.items.filter(isProduct) : [];
        const after = lines.reduce((total, line) => total + line.quantity, 0);

        // Nothing went in (the expected, all-or-nothing case): run the stock add. If the product
        // itself was the problem it returns the same error and the normal message shows.
        if (after < before + item.quantity) return stockAdd();

        // The product did go in without its gift. Do not add it again; just fetch the drawer.
        const ids = this.cart.getSectionsToRender().map((section) => section.id);
        const sections = await fetch(`${routes.cart_url}?sections=${ids.join(',')}`).then((response) =>
          response.json()
        );
        return { ...lines[0], sections };
      }

      // The stock form as a JSON line item - or null when it holds anything this does not
      // understand, or 'subscription' when a selling plan is chosen (no gift: out of scope).
      giftAddItem(formData) {
        const ignorable = ['form_type', 'utf8', 'product-id', 'section-id', 'sections', 'sections_url'];
        const item = { id: 0, quantity: 1 };
        for (const [key, value] of formData.entries()) {
          if (typeof value !== 'string') return null;
          if (key === 'id') item.id = Number(value);
          else if (key === 'quantity') item.quantity = Number(value) || 1;
          else if (key === 'selling_plan') {
            if (value) return 'subscription';
          } else if (key.startsWith('properties[') && key.endsWith(']')) {
            // A form post drops blank properties; do the same so the line key matches.
            if (value) {
              item.properties = item.properties || {};
              item.properties[key.slice(11, -1)] = value;
            }
          } else if (!ignorable.includes(key)) return null;
        }
        return item.id ? item : null;
      }

      // The two-request fallback, used only when giftAddItem() does not understand the form.
      // Runs AFTER the stock add has settled, and that request is untouched: every early exit
      // and every failure hands back the original response. It stays inside the promise chain
      // so the submit button remains disabled until the gift has landed and the drawer opens
      // once, with both lines.
      async addGift(response) {
        const giftId = this.dataset.gwpVariantId;
        if (!giftId) return response;

        try {
          // Only after a successful, one-time, single-line add that we can render a drawer for.
          if (response.status || !response.key || response.selling_plan_allocation || !this.cart) return response;

          // One gift per order. The drawer HTML the server rendered for THIS add is the authority:
          // unlike the page markup it cannot be stale after an earlier add, Back, or another tab.
          const drawer = response.sections && response.sections['cart-drawer'];
          if (!drawer || drawer.includes('data-gwp-line')) return response;

          // parent_line_key nests the gift under the product, so Shopify itself removes it when
          // the product goes - including at checkout, which theme code can never reach. _gwp is
          // the theme's own link, read by snippets/gwp-line-target.liquid. Content-Type is set
          // explicitly: the stock request above deletes it to let the browser write a multipart
          // boundary, and a JSON body sent that way is not parsed.
          const result = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(giftId),
                  quantity: 1,
                  parent_line_key: response.key,
                  properties: { _gwp: String(response.variant_id) },
                },
              ],
              sections: this.cart.getSectionsToRender().map((section) => section.id),
              sections_url: window.location.pathname,
            }),
          });
          const gift = await result.json();
          if (gift.status || !gift.sections) return response;

          // Keep the single-add shape every cartUpdate subscriber expects; swap in fresher HTML.
          return { ...response, sections: gift.sections };
        } catch (e) {
          console.error(e);
          return response;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
